/** The itinerary record's records-core wiring for VIEW mode (plan §5, §6, §10.3): what the
 *  page's `DraftProvider` and `RecordEditorProvider` need so every card's FieldSlot shows the
 *  stored value — from the detail payload alone, before any edit catalog exists.
 *
 *  - `itineraryBaselines(detail)` — the editable fields' values in their wire form (the shapes
 *    an `itinerary:<group id>` change carries, O10: ids for the currency, tour and line set,
 *    the regions', trip types', search tags' and access tags' ids, the month numbers, the day
 *    ids in order, the price as a number), so view mode reads exactly what the EDIT half will
 *    change — and each day's fields on its own `day:<id>` record. The route map's (and a day
 *    image's) wire form is its Image id, which the detail does not carry (only its CDN keys);
 *    view mode holds those keys, which the card shows downsized — the category icon's
 *    precedent. The EDIT half seeds every baseline from edit_context instead.
 *  - `useItineraryViewConfig(detail)` — the catalog from the registry (the itinerary's fields
 *    and its days'), the names of picked records and the read-only values' provenance. Nothing previews a draft or saves: the dock
 *    shows the saved itinerary (O5), and Edit arrives with the EDIT half. */
import { useMemo } from 'react';

import type { ItineraryDetail } from '@/api/itineraries';
import type { ItineraryRecordEditableFieldKey } from '@/api/itineraryRecord';
import { normalizeCustomId } from '@/features/catalog/identity';
import { recordKey, type RecordKey } from '@/features/records/draft/changeSet';
import type { RecordEditorConfig, RecordField } from '@/features/records/editor/types';
// View mode plans and saves nothing.
import { useNoApply, useNoPreview } from '@/features/records/editor/viewOnly';

import { dayBaselines } from './days/daysModel';
import { ITINERARY_ENTITY, itineraryDayRecordFields, itineraryRecordFields } from './registry';

/** The record key of the itinerary (`itinerary:<group id>`). */
export const itineraryRec = (id: number): RecordKey => recordKey(ITINERARY_ENTITY, id);

/** The price as the changeset sends it: a number (the detail's decimal string), or null. */
function costValue(cost: string | null | undefined): number | null {
  const raw = cost?.trim();
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

const ids = (refs: readonly { id: number }[]) => refs.map((r) => r.id);

/** The editable fields' stored values, in the wire form of their changes (the route map as
 *  its CDN keys — see above; the custom ID by the identity rule, `normalizeCustomId`). */
export function itineraryBaselineValues(d: ItineraryDetail): Record<ItineraryRecordEditableFieldKey, unknown> {
  return {
    name: d.name,
    description: d.description,
    duration_display: d.duration_display,
    cost: costValue(d.cost),
    cost_prefix: d.cost_prefix,
    cost_unit: d.cost_unit,
    currency: d.currency?.id ?? null,
    catalog_display_currency: d.catalog_display_currency,
    ontrip_display_currency: d.ontrip_display_currency,
    cta_button_behavior: d.cta_button_behavior,
    cta_button_text: d.cta_button_text,
    booking_url: d.booking_url,
    cta_tour: d.cta_tour?.id ?? null,
    cta_tour_departure: d.cta_tour_departure,
    days: ids(d.days),
    highlights: d.highlights,
    notes: d.notes,
    packing_tips: d.packing_tips,
    footer: d.footer,
    route_map: d.route_map,
    published: d.published,
    show_in_catalog: d.show_in_catalog,
    show_in_region_search: d.show_in_region_search,
    show_in_trip_type_search: d.show_in_trip_type_search,
    show_in_text_search: d.show_in_text_search,
    custom_day_titles: d.custom_day_titles,
    sequence: d.sequence,
    months: [...d.months].sort((a, b) => a - b),
    regions: ids(d.regions),
    trip_types: ids(d.trip_types),
    tags: ids(d.tags),
    atag: ids(d.atag),
    custom_id: normalizeCustomId(d.custom_id),
    vehicle_route_set: d.vehicle_route_set?.id ?? null,
  };
}

/** The draft's baselines (`"itinerary:<group id>"` → field → value), and each day's
 *  (`"day:<id>"`, `dayBaselines`). */
export function itineraryBaselines(d: ItineraryDetail): Record<RecordKey, Record<string, unknown>> {
  return { [itineraryRec(d.id)]: itineraryBaselineValues(d), ...dayBaselines(d.days) };
}

/** A button behaviour left null: what the App button card's Button behaviour reads. */
export const INHERITS_BEHAVIOUR = 'Inherit the operator’s';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The months it runs, "Jan, Feb, Dec" — or "Any month" when none is set (the app's rule). */
export function monthsText(months: readonly number[]): string {
  if (months.length === 0) return 'Any month';
  return [...months]
    .sort((a, b) => a - b)
    .map((m) => MONTH_ABBR[m - 1] ?? `#${m}`)
    .join(', ');
}

const isEmpty = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

/** Names for ids, from the detail's refs ("#<id>" for one the detail does not name). */
function namesOf(value: unknown, refs: readonly { id: number; name?: string | null }[]): string | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.map((id) => refs.find((r) => r.id === id)?.name?.trim() || `#${String(id)}`).join(', ');
}

/** A stored value as text where the generic format cannot word it (ids by name, the price,
 *  the months); undefined = the generic format. By key: the records core reads the id sets
 *  (regions, trip types, search tags, access tags) as its `tags` input. A day's field is the
 *  generic format's. */
export function formatItineraryValue(field: RecordField, value: unknown, d: ItineraryDetail): string | undefined {
  if (field.entity !== ITINERARY_ENTITY) return undefined;
  // The months say "Any month" when none is set — an answer, not an empty field.
  if (field.key === 'months') return Array.isArray(value) ? monthsText(value as number[]) : undefined;
  // A button behaviour left empty inherits the operator's (the card's verdict says which).
  if (field.key === 'cta_button_behavior' && value === null) return INHERITS_BEHAVIOUR;
  if (isEmpty(value)) return undefined;
  switch (field.key) {
    case 'cost':
      return typeof value === 'number' ? value.toLocaleString('en-US', { maximumFractionDigits: 2 }) : undefined;
    case 'currency':
      return d.currency && d.currency.id === value
        ? d.currency.symbol && d.currency.symbol !== d.currency.code
          ? `${d.currency.code} (${d.currency.symbol})`
          : d.currency.code
        : `#${String(value)}`;
    case 'cta_tour':
      return d.cta_tour && d.cta_tour.id === value ? d.cta_tour.name?.trim() || `Tour #${d.cta_tour.id}` : `#${String(value)}`;
    case 'vehicle_route_set':
      return d.vehicle_route_set && d.vehicle_route_set.id === value
        ? d.vehicle_route_set.name?.trim() || `Line set #${d.vehicle_route_set.id}`
        : `#${String(value)}`;
    case 'regions':
      return namesOf(value, d.regions);
    case 'trip_types':
      return namesOf(value, d.trip_types);
    case 'tags':
      return namesOf(value, d.tags);
    case 'atag':
      return namesOf(value, d.atag);
    default:
      return undefined;
  }
}

/** Where a read-only value comes from — its tooltip. */
const PROVENANCE: Readonly<Record<string, string>> = {
  cover_image: 'The first gallery image — set by the gallery order',
  operator_cta_default: 'The operator’s button behaviour: what a button set to inherit does',
  ext_id: 'The import identity, derived from the custom ID',
  branchio_link: 'Made by Branch.io in the background',
  qr_image_url: 'The deep link’s stored QR image',
  source: 'The newest import that touched the itinerary',
  default_itinerary: 'The catalog itinerary (day plan) it shows, and the itineraries sharing it',
};

/** The itinerary's fields and its days' (`day:<id>`), told apart by entity. */
const ITINERARY_FIELDS = [...itineraryRecordFields(), ...itineraryDayRecordFields()];
const NONE: never[] = [];

export function useItineraryViewConfig(detail: ItineraryDetail): RecordEditorConfig {
  return useMemo<RecordEditorConfig>(
    () => ({
      fields: ITINERARY_FIELDS,
      options: NONE,
      actions: NONE,
      effects: NONE,
      timezone: null,
      recordNoun: 'itinerary',
      managedBy: detail.source?.name ?? null,
      description: false,
      verdictOf: () => null,
      formatValue: (field, _rec, value) => formatItineraryValue(field, value, detail),
      provenanceOf: (field) => PROVENANCE[field.key],
      usePreview: useNoPreview,
      useApply: useNoApply,
    }),
    [detail],
  );
}
